import { defaultAllSelection } from "services/constants/selections";
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import * as Mui from "@mui/material";
import { MoreVert } from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { useSaveView } from "components/CustomComponents/useSaveView";
import { FilterProvider } from "../../FilterContext";
import { DoubleArrow } from "@mui/icons-material";
import {
  hrmsEmployeeFliter,
  statusType,
  themes,
  drawerStyle,
} from "services/constants";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { useLocation } from "react-router-dom";
import { useQuickActionFromUrl } from "components/GlobalSearch/useQuickActionFromUrl";
import { showToast } from "services/utils/Status";
import { CustomButton } from "components/CustomComponents/CustomButton";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { CustomTextBox } from "components/CustomComponents";
import { GlobalDialogBox } from "components/GlobalDialogBox";
import { setGlobalSearchValue } from "services/Redux/userToken";
import { setScreenUserId } from "services/Redux/HRPortal";
import { Loader } from "components/Loader";
import { CustomAutoComplete } from "components/CustomComponents/CustomAutoComplete";
import { useFetch } from "services/hooks/useFetch";
import sessionHandling from "services/utils/notificationUtils";
import { formKeyText } from "services/constants/Styles";
import { ApprovalDateRangeFilter } from "components/CustomComponents/ApprovalDateRangeFilter";
import { useForm } from "react-hook-form";
import moment from "moment";
import { CreateUserEmployee } from "./CreateUserEmployee";
import { EditUserEmployee } from "./EditUserEmployee";
import { HREmployeeTable } from "../Headers";
import dayjs from "dayjs";
import { BulkReprocess } from "./BulkReprocess";
import TaskProgressCard from "components/CustomComponents/TaskProgressCard";
import { ActivePeriod } from "./ActivePeriod";
import { customAPI, customGetAPI } from "services/utils/CustomAPI";
import { infoStyle } from "services/constants";
import { setFilterApi } from "services/Redux/userManagament";
import {
  checkEditorPermission,
  checkViewerPermission,
} from "services/utils/HelperFunction";

const FINISHED_TASK_STATUSES = ["SUCCESS", "FAILURE", "FAILED", "COMPLETED", "CANCELLED", "REVOKED"];

// A reprocess task is running until the backend marks it completed or sends
// a finished status. Phase names (DELETING, DISPATCHING, CALCULATING, ...)
// all count as running; the old PROGRESS/PENDING-only check hid the progress
// card while the per-task poll showed it again. is_stale is ignored: it can
// flip on while workers are calculating, which hid the card mid-run.
const isReprocessTaskRunning = (task) =>
  Boolean(task) &&
  !task.is_completed &&
  !FINISHED_TASK_STATUSES.includes(String(task.status || "").toUpperCase());

// Both progress endpoints feed the same card. Fields one response leaves
// out (createdby, created_at, ...) keep the value the other one sent for the
// same task, so the card's details don't jump between polls.
const reprocessTaskProgress = (task, prev, estimatedTime) => {
  const same = prev?.taskId && prev.taskId === task.task_id;
  const keep = (value, key, fallback = null) =>
    value ?? (same ? prev[key] : undefined) ?? fallback;
  return {
    active: isReprocessTaskRunning(task),
    progress: task.progress ?? 0,
    message: task.message || "Processing update...",
    status: task.status || "PROGRESS",
    title: "Bulk Reprocessing",
    initiatedBy: keep(task.createdby ?? task.metadata?.initiated_by, "initiatedBy", "System"),
    estimatedTime,
    shiftCategoryName: "User Details",
    taskId: task.task_id,
    created_at: keep(task.created_at, "created_at"),
    updated_at: keep(task.updated_at, "updated_at"),
    duration: keep(task.duration, "duration"),
    image_url: keep(task.image_url, "image_url"),
  };
};

export const EmployeeDetails = ({
  setUserProcess,
  userProcess,
  setTaskProgress,
  taskProgress,
}) => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const {
    domain,
    token,
    globalSearchValue,
    adminCompanyId,
    userDetails,
    topbarCompanyId,
    topbarCompanyName,
    userScreenAccess,
  } = useSelector((state) => state.tokenReducer);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [employeeDetails, setEmployeeDetails] = useState([]);
  const [openDialogBox, setOpenDialogBox] = useState(false);
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [editDetails, setEditDetails] = useState(null);
  const [openDeactivatePopup, setOpenDeactivatePopup] = useState(false);
  const [openActivatePopup, setOpenActivatePopup] = useState(false);
  const [openAssignmentCheckPopup, setOpenAssignmentCheckPopup] =
    useState(false);
  const [isReactivationEdit, setIsReactivationEdit] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [deactivateEffectiveTo, setDeactivateEffectiveTo] = useState(
    dayjs().format("YYYY-MM-DD"),
  );
  const [activateEffectiveFrom, setActivateEffectiveFrom] = useState(
    dayjs().format("YYYY-MM-DD"),
  );
  const [pendingActivation, setPendingActivation] = useState(null);
  const [masterEmployee, setMasterEmployee] = useState([]);
  const [fetchUserDetails, setFetchUserDetails] = useState(true);
  const [buttonLoader, setButtonLoader] = useState(false);
  const [departmentName, setDepartmentName] = useState("All Department");
  const [statusName, setStatusName] = useState("Active");
  const [departmentId, setDepartmentId] = useState(String(defaultAllSelection));
  const [bulkReprocess, setBulkReprocess] = useState(false);
  const [bulkReprocessExpanded, setBulkReprocessExpanded] = useState(false);
  const [openActivePeriodDialog, setOpenActivePeriodDialog] = useState(false);
  const [reprocessStartDate, setReprocessStartDate] = useState("");
  const [reprocessEndDate, setReprocessEndDate] = useState("");
  const [reprocessDateRangeLabel, setReprocessDateRangeLabel] =
    useState("Yesterday");
  const pollingIntervalRef = useRef(null);

  // --- Bulk Import Menu State ---
  const [importAnchorEl, setImportAnchorEl] = useState(null);
  const openImportMenu = Boolean(importAnchorEl);

  const { result: allDepartmentDetails } = useFetch(
    `${domain}get-department/?user_id=${userDetails?.id}&company_id=${topbarCompanyId}`,
  );
  const {
    reset,
    register,
    setValue,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
  });

  const containerRef = useRef(null);
  const departmentDetails = [
    {
      id: defaultAllSelection,
      department_name: "All Department",
    },
    ...allDepartmentDetails,
  ];

  const shiftModule = userScreenAccess?.module_details?.find(
    (mod) => mod.module_name === "Employee",
  );
  const subModuleId = shiftModule?.submodules?.find(
    (sub) => sub.sub_module_name === "Employee",
  )?.sub_module_id;

  const canView = checkViewerPermission(
    userScreenAccess?.module_details,
    "Employee",
    "Employee",
  );
  const check_access =
    checkEditorPermission(
      userScreenAccess?.module_details,
      "Employee",
      "Employee",
    ) === true;

  const [isCreatePanelOpen, setIsCreatePanelOpen] = useState(false);
  const [isViewPanelOpen, setIsViewPanelOpen] = useState(false);

  const { visibleHeaders, titleOverride, newButtonComponent } = useSaveView({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "employee_details_saved_views",
    fullHeaders: HREmployeeTable,
    screenAccess: canView,
    onViewChange: () => setPage(0),
  });

  useEffect(() => {
    if (topbarCompanyId) {
      setFetchUserDetails(true);
    }
  }, [topbarCompanyId]);

  useEffect(() => {
    const fetchEmployeeDetails = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${domain}list/employee?company_id=${topbarCompanyId}&department_id=${departmentId}&status=${statusName}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `token ${token}`,
            },
          },
        );
        const res = await response.json();
        if (response.ok) {
          setLoading(false);
          setEmployeeDetails(res);
          setMasterEmployee(res);
          dispatch(setFilterApi(true));
        } else if (response.status === 409) {
          sessionHandling();
        } else {
          throw new Error("REQUEST_FAILED");
        }
      } catch (error) {
        console.error("[Employeedetails] operation failed:", error);
        showToast(
          "error",
          "Couldn't load employee details. Please refresh the page.",
        );
      }
    };
    if (fetchUserDetails) {
      fetchEmployeeDetails();
      setFetchUserDetails(false);
    }
  }, [
    adminCompanyId,
    departmentId,
    dispatch,
    domain,
    fetchUserDetails,
    statusName,
    token,
    topbarCompanyId,
  ]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCreateOpen = () => {
    setIsCreatePanelOpen(true);
  };

  const handleCreateClose = () => {
    setIsCreatePanelOpen(false);
  };

  const handleViewOpen = (data, reactivation = false) => {
    dispatch(setScreenUserId(data.user_id));
    localStorage.setItem("hrScreenUserId", data.user_id);
    setIsReactivationEdit(reactivation);
    setIsViewPanelOpen(true);
  };

  const handleViewClose = () => {
    setIsViewPanelOpen(false);
    setIsReactivationEdit(false);
    setPendingActivation(null);
    setOpenAssignmentCheckPopup(false);
  };

  // Top-bar search: "Add Employee" opens the create panel, and an employee
  // result (?employee=<user_id>) opens that employee's panel. Both wait for
  // this screen's permissions and follow them.
  const peopleAccessReady = userScreenAccess?.product_name === "People";
  useQuickActionFromUrl(
    check_access ? [{ label: "Add Employee", onClick: handleCreateOpen }] : [],
    peopleAccessReady,
  );
  const searchedEmployeeId = new URLSearchParams(location.search).get("employee");
  useEffect(() => {
    if (!searchedEmployeeId || !peopleAccessReady) return;
    const params = new URLSearchParams(location.search);
    params.delete("employee");
    const rest = params.toString();
    history.replace({ pathname: location.pathname, search: rest ? `?${rest}` : "" });
    if (canView) handleViewOpen({ user_id: searchedEmployeeId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchedEmployeeId, peopleAccessReady]);

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const deactivateEmployee = async () => {
    setButtonLoader(true);
    try {
      const response = await fetch(`${domain}deactivate/`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `token ${token}`,
        },
        body: JSON.stringify({
          user_id: editDetails.user_id,
          status: "Inactive",
          effective_to: deactivateEffectiveTo,
        }),
      });
      const res = await response.json();
      if (response.status === 201 && response.ok) {
        setAnchorEl(null);
        const field = "status";
        const updatedUsers = employeeDetails.map((element) => {
          if (element.user_id === editDetails.user_id) {
            return { ...element, [field]: "Inactive" };
          }
          return element;
        });
        setEmployeeDetails(updatedUsers);
        setFetchUserDetails(true);
        handleDeactivateDialogBox();
        showToast("success", res?.message);
      } else if (response.status === 409) {
        sessionHandling();
      } else if (response.status === 400 && res?.error) {
        // Surface the backend validation message (e.g. trying to deactivate
        // a department lead) instead of a generic error.
        showToast("error", res.error);
        handleDeactivateDialogBox();
      } else {
        throw new Error("REQUEST_FAILED");
      }
    } catch (error) {
      console.error("[Employeedetails] operation failed:", error);
      showToast("error", "Couldn't save employee details. Please try again.");
      handleDeactivateDialogBox();
    } finally {
      setButtonLoader(false);
    }
  };

  const activateEmployee = async (activationDetails = pendingActivation) => {
    const targetUserId = activationDetails?.userId ?? editDetails?.user_id;
    const effectiveFrom =
      activationDetails?.effectiveFrom ?? activateEffectiveFrom;
    if (!targetUserId) {
      showToast("error", "Please select an employee to activate.");
      return false;
    }
    setButtonLoader(true);
    try {
      const response = await fetch(`${domain}deactivate/`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `token ${token}`,
        },
        body: JSON.stringify({
          user_id: targetUserId,
          status: "Active",
          effective_from: effectiveFrom,
          effective_to: null,
        }),
      });
      const res = await response.json();
      if (response.status === 201 && response.ok) {
        setAnchorEl(null);
        showToast("success", res?.message);
        if (res?.warning) {
          showToast("info", res.warning);
        }
        const field = "status";
        const updatedUsers = employeeDetails.map((element) => {
          if (element.user_id === targetUserId) {
            return { ...element, [field]: "Active" };
          }
          return element;
        });
        setEmployeeDetails(updatedUsers);
        setFetchUserDetails(true);
        setPendingActivation(null);
        setActivateEffectiveFrom(dayjs().format("YYYY-MM-DD"));
        return true;
      } else if (response.status === 409) {
        sessionHandling();
      } else if (response.status === 400 && (res?.error || res?.message)) {
        showToast("error", res.error || res.message);
      } else {
        throw new Error("REQUEST_FAILED");
      }
    } catch (error) {
      console.error("[Employeedetails] operation failed:", error);
      showToast("error", "Couldn't save employee details. Please try again.");
    } finally {
      setButtonLoader(false);
    }
    return false;
  };

  const handleStartActivationReview = () => {
    if (!editDetails?.user_id) {
      showToast("error", "Please select an employee to activate.");
      return;
    }
    const activationRequest = {
      userId: editDetails.user_id,
      effectiveFrom: activateEffectiveFrom,
    };
    setPendingActivation(activationRequest);
    setAnchorEl(null);
    setOpenActivatePopup(false);
    setIsCreatePanelOpen(false);
    setBulkReprocess(false);
    setOpenActivePeriodDialog(false);
    handleViewOpen(editDetails, true);
    setOpenAssignmentCheckPopup(true);
  };

  const reprocessAttendance = async () => {
    const userId = editDetails?.user_id ?? editDetails?.id;
    const companyId =
      editDetails?.company_id ?? topbarCompanyId ?? adminCompanyId;
    const selectedStartDate = dayjs(reprocessStartDate);
    const selectedEndDate = dayjs(reprocessEndDate);
    const numericUserId = Number(userId);
    const numericCompanyId = Number(companyId);

    if (!userId || Number.isNaN(numericUserId)) {
      showToast("error", "Please select an employee to reprocess.");
      return;
    }

    if (!companyId || Number.isNaN(numericCompanyId)) {
      showToast("error", "Please select a company to reprocess attendance.");
      return;
    }

    if (!selectedStartDate.isValid()) {
      showToast("error", "Please select a valid start date.");
      return;
    }

    if (!selectedEndDate.isValid()) {
      showToast("error", "Please select a valid end date.");
      return;
    }

    if (selectedEndDate.isBefore(selectedStartDate, "day")) {
      showToast("error", "End date cannot be before start date.");
      return;
    }

    const payload = {
      user_id: numericUserId,
      start_date: selectedStartDate.format("YYYY-MM-DD"),
      end_date: selectedEndDate.format("YYYY-MM-DD"),
    };

    setButtonLoader(true);
    try {
      const response = await customAPI(
        `${domain}bulk-ar-reprocess/`,
        "POST",
        payload,
      );

      if (response?.ok) {
        const res = response.responseDetails;
        setAnchorEl(null);
        showToast(
          "success",
          res?.message || "Biometric reprocessing queued successfully.",
        );
        if (res?.task_id && setTaskProgress) {
          setTaskProgress({
            active: true,
            progress: 0,
            message: res?.message || "Starting background processing...",
            status: "PENDING",
            title: "Reprocessing",
            initiatedBy: userDetails?.user_name ?? "System",
            estimatedTime: "Calculating...",
            shiftCategoryName: "User Details",
            taskId: res.task_id,
            created_at: null,
            updated_at: null,
            duration: null,
            image_url: null,
          });
        }
        setFetchUserDetails(true);
        handleReprocessDialogBox();
      } else {
        throw new Error(response?.message || "Request failed");
      }
    } catch (error) {
      console.error("[Employeedetails] operation failed:", error);
      showToast(
        "error",
        "Couldn't reprocess the attendance. Please try again.",
      );
    } finally {
      setButtonLoader(false);
    }
  };

  const handleReprocessConfirm = async (event) => {
    event?.preventDefault();
    event?.stopPropagation();

    if (!reprocessStartDate || !reprocessEndDate) {
      showToast("error", "Please select a date range to reprocess.");
      return;
    }

    reprocessAttendance();
  };

  const handleReprocessDateRange = (rangeStart, rangeEnd, rangeLabel) => {
    const normalizedStart = dayjs(rangeStart).format("YYYY-MM-DD");
    const normalizedEnd = dayjs(rangeEnd).format("YYYY-MM-DD");
    setReprocessStartDate(normalizedStart);
    setReprocessEndDate(normalizedEnd);
    setReprocessDateRangeLabel(
      typeof rangeLabel === "string" && rangeLabel.trim()
        ? rangeLabel
        : "Custom Range",
    );
  };

  const handleDeactivateDialogBox = () => {
    setOpenDeactivatePopup(false);
    setAnchorEl(null);
    setDeactivateEffectiveTo(dayjs().format("YYYY-MM-DD"));
  };

  const handleActivateDialogBox = () => {
    setOpenActivatePopup(false);
    setAnchorEl(null);
    setActivateEffectiveFrom(dayjs().format("YYYY-MM-DD"));
  };

  // Close the post-activation confirmation box. The edit page stays open so
  // HR can review/correct the assignments (there is no skip option).
  const handleAssignmentCheckClose = () => {
    setOpenAssignmentCheckPopup(false);
  };

  const handleReprocessDialogBox = () => {
    setOpenDialog(false);
    setAnchorEl(null);
    reset();
  };

  const handleActivePeriodClose = () => {
    setOpenActivePeriodDialog(false);
  };

  const handleCloseInfoDialog = () => {
    setOpenDialogBox(false);
  };

  // --- Bulk Import Handlers ---
  const handleImportClick = (event) => {
    // Attempt to capture the anchor element safely
    setImportAnchorEl(event.currentTarget);
  };

  const handleImportClose = () => {
    setImportAnchorEl(null);
  };

  const handleUserImportSelect = () => {
    handleImportClose();
    dispatch(setGlobalSearchValue(""));
    history.push("/admin/hrzone/bulk/import");
  };

  const handleBankImportSelect = () => {
    handleImportClose();
    dispatch(setGlobalSearchValue(""));
    history.push("/admin/hrzone/bulk/bank-import");
  };

  // --- ADDED NEW HANDLER FOR SALARY IMPORT ---
  const handleSalaryImportSelect = () => {
    handleImportClose();
    dispatch(setGlobalSearchValue(""));
    history.push("/admin/hrzone/bulk/salary-import");
  };

  const handleEmployeeUpdateSelect = () => {
    handleImportClose();
    dispatch(setGlobalSearchValue(""));
    history.push("/admin/hrzone/bulk/employee-update");
  };

  useEffect(() => {
    const filteredResults = masterEmployee.filter((item) =>
      hrmsEmployeeFliter.some((key) =>
        item[key]
          ?.toString()
          .toLowerCase()
          .includes(globalSearchValue.toString().toLowerCase()),
      ),
    );
    setEmployeeDetails(filteredResults);
    setPage(globalSearchValue ? 0 : page);
  }, [globalSearchValue, masterEmployee, page]);

  const handleDepartmentName = (event, data) => {
    if (data) {
      setDepartmentId(data?.id);
      setDepartmentName(data?.label);
      setTimeout(function () {
        setFetchUserDetails(true);
      }, 150);
      setPage(0);
    } else {
      setDepartmentId("");
      setDepartmentName("");
    }
  };

  const handleStatusName = (event, data) => {
    if (data) {
      setStatusName(data?.name);
      setFetchUserDetails(true);
      setPage(0);
    } else {
      setStatusName("");
    }
  };

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  }, [page]);

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      setEmployeeDetails(filteredData);
      if (page * rowsPerPage >= filteredData.length) setPage(0);
    },
    [page, rowsPerPage],
  );

  const calculateRemainingTime = (progress) => {
    console.log(progress);
    if (progress === 0) return "Calculating...";
    if (progress >= 100) return "Completed";

    const elapsedMinutes = 2;
    const totalEstimatedMinutes = (elapsedMinutes * 100) / progress;
    const remainingMinutes = totalEstimatedMinutes - elapsedMinutes;

    if (remainingMinutes < 1) return "Less than 1 minute";
    if (remainingMinutes < 60) return `${Math.ceil(remainingMinutes)} mins`;

    const hours = Math.floor(remainingMinutes / 60);
    const minutes = Math.ceil(remainingMinutes % 60);
    return `${hours} hr ${minutes} mins`;
  };

  const pollTaskStatus = async (taskId) => {
    if (!taskId) return;

    try {
      const url = `${domain}biometric-particular-reprocess/progress/?task_id=${taskId}`;
      const response = await customGetAPI(url);

      if (response.ok && response.responseDetails.data) {
        const data = response.responseDetails.data;

        setTaskProgress((prev) =>
          reprocessTaskProgress(
            data,
            prev,
            calculateRemainingTime(data.progress || 0),
          ),
        );

        if (!isReprocessTaskRunning(data)) {
          showToast("success", "Task completed.");
          setTimeout(() => {
            setTaskProgress((prev) => ({
              ...prev,
              active: false,
              progress: 100,
              status: "SUCCESS",
            }));
          }, 3000);
        } else {
          setTimeout(() => pollTaskStatus(taskId), 5000);
        }
      }
    } catch (error) {
      console.error("Error polling task status:", error);
      setTimeout(() => pollTaskStatus(taskId), 10000);
    }
  };

  const checkActiveTasks = useCallback(async () => {
    try {
      const url = `${domain}biometric-reprocess/progress/`;
      const response = await customGetAPI(url);

      if (!response?.ok) return;

      const task = response?.responseDetails?.data;

      if (!task) return;

      if (!isReprocessTaskRunning(task)) return;

      setTaskProgress((prev) =>
        reprocessTaskProgress(
          task,
          prev,
          calculateRemainingTime(task.progress ?? 0),
        ),
      );
    } catch (error) {
      console.error("Error checking active tasks:", error);
    }
  }, [domain, setTaskProgress]);

  useEffect(() => {
    if (!isCreatePanelOpen && !isViewPanelOpen && !bulkReprocess) {
      checkActiveTasks();
    }
  }, [
    bulkReprocess,
    checkActiveTasks,
    domain,
    isCreatePanelOpen,
    isViewPanelOpen,
  ]);

  useEffect(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    pollingIntervalRef.current = setInterval(async () => {
      try {
        const url = `${domain}biometric-reprocess/progress/`;
        const response = await customGetAPI(url);

        if (!response?.ok) return;

        const task = response?.responseDetails?.data;

        if (!task) return;

        if (!isReprocessTaskRunning(task)) {
          setTaskProgress((prev) => ({
            ...prev,
            active: false,
          }));
          return;
        }

        setTaskProgress((prev) =>
          reprocessTaskProgress(
            task,
            prev,
            calculateRemainingTime(task.progress ?? 0),
          ),
        );

        if (isCreatePanelOpen || isViewPanelOpen) {
          setIsCreatePanelOpen(false);
          setIsViewPanelOpen(false);

          showToast(
            "info",
            "Detected background task in progress. Returning to main view to display progress.",
          );
        }
      } catch (error) {
        console.error("Error in polling interval:", error);
      }
    }, 5000);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
    // Kept other dependencies that actually trigger logic changes
  }, [domain, isCreatePanelOpen, isViewPanelOpen, setTaskProgress]);

  const handleEffectiveTo = (event) => {
    setValue("effective_to_date", event.target.value);
    setDeactivateEffectiveTo(event.target.value);
  };

  const handleEffectiveFrom = (event) => {
    setValue("effective_from_date", event.target.value);
    setActivateEffectiveFrom(event.target.value);
  };

  // Attendance reprocessing only ever makes sense for closed/past days, so
  // "Today" and "This Month" (which can span into the future) are dropped
  // from the preset list — only Yesterday, Last 7/30 Days and Last Month
  // remain, alongside the picker's own custom-range calendar.
  const reprocessDateRanges = useMemo(
    () => ({
      Yesterday: [
        dayjs().subtract(1, "day").toDate(),
        dayjs().subtract(1, "day").toDate(),
      ],
      "Last 7 Days": [
        dayjs().subtract(7, "day").toDate(),
        dayjs().subtract(1, "day").toDate(),
      ],
      "Last 30 Days": [
        dayjs().subtract(30, "day").toDate(),
        dayjs().subtract(1, "day").toDate(),
      ],
      "Last Month": [
        dayjs().subtract(1, "month").startOf("month").toDate(),
        dayjs().subtract(1, "month").endOf("month").toDate(),
      ],
    }),
    [],
  );

  const reportMetadata = useMemo(
    () => ({
      "Company Name": topbarCompanyName || "N/A",
      "Department Name": departmentName || "N/A",
      Status: statusName || "N/A",
      "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
    }),
    [departmentName, statusName, topbarCompanyName],
  );

  return (
    <>
      <React.Fragment>
        <Mui.Grid
          container
          alignItems="center"
          justifyContent="space-between"
          mb={1}
          flexWrap="wrap"
        >
          <Mui.Grid item>
            <Mui.Stack direction="Row">
              <Mui.Box>
                <Mui.Typography variant="h6" fontWeight={600}>
                  Overview
                </Mui.Typography>
                <Mui.Typography variant="body2" color="text.secondary">
                  {moment().format("dddd Do MMMM, YYYY")}
                </Mui.Typography>
              </Mui.Box>
            </Mui.Stack>
          </Mui.Grid>
        </Mui.Grid>

        {taskProgress?.active && taskProgress?.taskId && (
          <TaskProgressCard
            taskProgress={taskProgress}
            isEmployeeScreen={true}
          />
        )}

        <Mui.Grid
          container
          sx={{
            pointerEvents: taskProgress.active ? "none" : "auto",
            opacity: taskProgress.active ? 0.5 : 1,
          }}
        >
          <Mui.Grid item xs={2} sx={{ padding: 1, paddingTop: 3 }}>
            <Mui.Typography>Department</Mui.Typography>
            <CustomAutoComplete
              dataList={departmentDetails.map((obj) => ({
                ...obj,
                label: obj.department_name,
              }))}
              selectedValue={departmentName}
              updateDataFunction={handleDepartmentName}
              placeHolder="Select the department name"
            />
          </Mui.Grid>
          <Mui.Grid item xs={2} sx={{ padding: 1, paddingTop: 3 }}>
            <Mui.Typography>Status</Mui.Typography>
            <CustomAutoComplete
              dataList={statusType.map((obj) => ({
                ...obj,
                label: obj.name,
              }))}
              selectedValue={statusName}
              updateDataFunction={handleStatusName}
              placeHolder="Select the status name"
            />
          </Mui.Grid>

          <Mui.Grid item xs={4.1}></Mui.Grid>
          <Mui.Grid
            item
            xs={3.9}
            sx={{
              paddingTop: 2,
              padding: 1,
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-end",
              alignItems: "flex-end",
            }}
          >
            {/* --- Updated Bulk Import Dropdown Section --- */}
            {check_access && (
              <Mui.Box sx={{ display: "inline-block" }}>
                <Mui.Box
                  onClick={handleImportClick}
                  sx={{ cursor: "pointer", display: "inline-block" }}
                >
                  <CustomButton
                    actionFuntion={(e) => {
                      if (e && e.currentTarget) handleImportClick(e);
                    }}
                    actionName="Bulk Import"
                    typeName="button"
                  />
                </Mui.Box>
                <Mui.Menu
                  anchorEl={importAnchorEl}
                  open={openImportMenu}
                  onClose={handleImportClose}
                  PaperProps={{
                    elevation: 3,
                    sx: { mt: 1, minWidth: 150 },
                  }}
                >
                  <Mui.MenuItem onClick={handleUserImportSelect}>
                    Employee Import
                  </Mui.MenuItem>
                  <Mui.MenuItem onClick={handleEmployeeUpdateSelect}>
                    Employee Update
                  </Mui.MenuItem>
                  <Mui.MenuItem onClick={handleBankImportSelect}>
                    Bank Import
                  </Mui.MenuItem>
                  <Mui.MenuItem onClick={handleSalaryImportSelect}>
                    Salary Import
                  </Mui.MenuItem>
                </Mui.Menu>
              </Mui.Box>
            )}
            {check_access && <>&nbsp;&nbsp;</>}
            {/* ------------------------------------------- */}
            {check_access && (
              <CustomButton
                actionFuntion={() => {
                  setBulkReprocessExpanded(false);
                  setBulkReprocess(true);
                }}
                actionName="Bulk Reprocess"
                typeName="button"
              />
            )}
            {check_access && <>&nbsp;&nbsp;</>}
            {check_access && (
              <CustomButton
                actionFuntion={handleCreateOpen}
                actionName="Add Employee"
                typeName="button"
              />
            )}
          </Mui.Grid>
        </Mui.Grid>
        {loading ? (
          <Loader info="Loading..." />
        ) : (
          <FilterProvider
            domain={domain}
            userDetails={userDetails}
            subModuleId={subModuleId}
          >
            <Mui.Paper
              sx={{
                width: "100%",
                height: "78vh",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                pointerEvents: taskProgress.active ? "none" : "auto",
                opacity: taskProgress.active ? 0.5 : 1,
              }}
            >
              <CustomFilter
                title={`${statusName} Employee`}
                titleOverride={titleOverride}
                filterHeaders={visibleHeaders}
                data={masterEmployee}
                filteredData={employeeDetails}
                onFilteredDataChange={handleFilteredDataChange}
                screenAccess={canView}
                newButtonComponent={newButtonComponent}
                downloadFormats={["excel", "pdf", "csv"]}
                includeMetadata={true}
                metadata={reportMetadata}
              />
              <Mui.TableContainer
                component={Mui.Paper}
                ref={containerRef}
                sx={{
                  width: "100%",
                  flex: 1,
                  minHeight: 0,
                  overflow: "auto",
                  ml: 2,
                  mr: 2,
                }}
              >
                <Mui.Table>
                  <Mui.TableHead
                    sx={{
                      backgroundColor: themes.primary,
                      position: "sticky",
                      top: -1,
                      zIndex: 10,
                      "& .MuiTableCell-root": {
                        height: "15px",
                        padding: "6px 35px",
                      },
                    }}
                  >
                    <Mui.TableRow maxWidth="xl" align="left">
                      {visibleHeaders?.map((value) => (
                        <Mui.TableCell
                          key={value?.key}
                          sx={{
                            color: themes.headLine,
                            fontWeight: "bold",
                          }}
                        >
                          {value?.name}
                        </Mui.TableCell>
                      ))}
                    </Mui.TableRow>
                  </Mui.TableHead>
                  <Mui.TableBody>
                    {employeeDetails?.length === 0 ? (
                      <TableEmptyState dataLength={masterEmployee} />
                    ) : (
                      employeeDetails
                        ?.slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage,
                        )
                        .map((data, index) => (
                          <Mui.TableRow
                            key={data.id || index}
                            sx={{
                              "& .MuiTableCell-root": {
                                height: "15px",
                                padding: "6px 35px",
                              },
                            }}
                          >
                            {visibleHeaders.map((head) =>
                              head.key === "user_name" ? (
                                <Mui.TableCell
                                  key={head.key}
                                  align="left"
                                  sx={{
                                    cursor: check_access
                                      ? "pointer"
                                      : "default",
                                  }}
                                  onClick={() => {
                                    if (!check_access) return;
                                    setEditDetails(data);
                                    handleViewOpen(data);
                                  }}
                                >
                                  <Mui.Stack direction="Row">
                                    <Mui.Avatar
                                      sx={{ width: 23, height: 23 }}
                                      alt={data?.user_name
                                        ?.charAt(0)
                                        .toUpperCase()}
                                      src={data.image_url}
                                    />
                                    <Mui.Typography
                                      sx={{
                                        paddingLeft: 1,
                                      }}
                                    >
                                      {data.user_name ? data.user_name : "N/A"}
                                    </Mui.Typography>
                                  </Mui.Stack>
                                </Mui.TableCell>
                              ) : head.key === "" ? (
                                <Mui.TableCell key={head.key} align="left">
                                  {check_access && (
                                    <>
                                      <Mui.IconButton
                                        size="small"
                                        onClick={(e) => {
                                          setEditDetails(data);
                                          setAnchorEl(e.currentTarget);
                                        }}
                                      >
                                        <MoreVert
                                          size="small"
                                          sx={{
                                            color: themes.headLine,
                                            strokeWidth: 2,
                                          }}
                                        />
                                      </Mui.IconButton>

                                      <Mui.Menu
                                        anchorEl={anchorEl}
                                        // Every row renders a menu, so only the
                                        // row whose ⋮ was clicked may open; the
                                        // shared anchorEl alone opened one menu
                                        // per row, stacked on top of each other.
                                        open={
                                          Boolean(anchorEl) &&
                                          editDetails === data
                                        }
                                        onClose={handleMenuClose}
                                        elevation={1}
                                        transformOrigin={{
                                          horizontal: "right",
                                          vertical: "top",
                                        }}
                                        anchorOrigin={{
                                          horizontal: "right",
                                          vertical: "bottom",
                                        }}
                                      >
                                        {editDetails?.status === "Active" ? (
                                          <Mui.MenuItem
                                            onClick={() => {
                                              setAnchorEl(null);
                                              setDeactivateEffectiveTo(
                                                dayjs().format("YYYY-MM-DD"),
                                              );
                                              setOpenDeactivatePopup(true);
                                            }}
                                          >
                                            Deactivate
                                          </Mui.MenuItem>
                                        ) : (
                                          <Mui.MenuItem
                                            onClick={() => {
                                              setAnchorEl(null);
                                              setActivateEffectiveFrom(
                                                dayjs().format("YYYY-MM-DD"),
                                              );
                                              setOpenActivatePopup(true);
                                            }}
                                          >
                                            Activate
                                          </Mui.MenuItem>
                                        )}
                                        <Mui.MenuItem
                                          onClick={() => {
                                            setAnchorEl(null);
                                            const yesterday = dayjs()
                                              .subtract(1, "day")
                                              .format("YYYY-MM-DD");
                                            setReprocessStartDate(yesterday);
                                            setReprocessEndDate(yesterday);
                                            setReprocessDateRangeLabel(
                                              "Yesterday",
                                            );
                                            setOpenDialog(true);
                                          }}
                                        >
                                          Reprocess Attendance
                                        </Mui.MenuItem>
                                        <Mui.MenuItem
                                          onClick={() => {
                                            setAnchorEl(null);
                                            setIsCreatePanelOpen(false);
                                            setIsViewPanelOpen(false);
                                            setBulkReprocess(false);
                                            setOpenActivePeriodDialog(true);
                                          }}
                                        >
                                          View Active Period
                                        </Mui.MenuItem>
                                      </Mui.Menu>
                                    </>
                                  )}
                                </Mui.TableCell>
                              ) : (
                                <Mui.TableCell
                                  key={head.key}
                                  align="left"
                                  sx={{
                                    cursor: check_access
                                      ? "pointer"
                                      : "default",
                                  }}
                                  onClick={() => {
                                    if (!check_access) return;
                                    setEditDetails(data);
                                    handleViewOpen(data);
                                  }}
                                >
                                  {data[head.key]}
                                </Mui.TableCell>
                              ),
                            )}
                          </Mui.TableRow>
                        ))
                    )}
                  </Mui.TableBody>
                </Mui.Table>
              </Mui.TableContainer>
              {employeeDetails.length > 25 && (
                <Mui.TablePagination
                  className="custom-pagination"
                  component="div"
                  sx={{ flexShrink: 0 }}
                  rowsPerPageOptions={[25, 50, 75, 100]}
                  count={employeeDetails.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              )}
            </Mui.Paper>
          </FilterProvider>
        )}

        <Mui.Drawer
          anchor="right"
          open={isCreatePanelOpen || isViewPanelOpen}
          onClose={() => {
            isCreatePanelOpen ? handleCreateClose() : handleViewClose();
          }}
          sx={drawerStyle}
        >
          {isCreatePanelOpen ? (
            <CreateUserEmployee
              onClose={handleCreateClose}
              reCallApi={setFetchUserDetails}
            />
          ) : isViewPanelOpen ? (
            <EditUserEmployee
              onClose={handleViewClose}
              reCallApi={setFetchUserDetails}
              isReactivationEdit={isReactivationEdit}
              onReactivationUpdateComplete={
                pendingActivation
                  ? () => activateEmployee(pendingActivation)
                  : null
              }
            />
          ) : null}
        </Mui.Drawer>

        <GlobalDialogBox
          open={openDialogBox}
          handleCloseDialog={handleCloseInfoDialog}
          title="Warning"
        >
          <Mui.Typography>
            `To onboard an employee, please create a designation and leave the
            group before proceeding. Designations can be created by going to
            Administration {">"} Masters {">"} Add Designation. Leave group can
            be created by going to Team Zone {">"}Leave Group {">"}Add Leave
            Group`
          </Mui.Typography>
          <Mui.Grid
            container
            sx={{ justifyContent: "flex-end", paddingTop: 1 }}
          >
            <Mui.Button
              variant="contained"
              size="small"
              sx={{
                background: themes.primaryIndicator,
                color: themes.headLine,
                padding: 1,
              }}
              onClick={() => handleCloseInfoDialog()}
            >
              Ok
            </Mui.Button>
          </Mui.Grid>
        </GlobalDialogBox>
        <GlobalDialogBox
          open={openDeactivatePopup}
          handleCloseDialog={handleDeactivateDialogBox}
          title={"Deactivate the user"}
        >
          <Mui.Typography sx={{ fontSize: 22 }}>
            Do you want to deactivate the employee?
          </Mui.Typography>
          <Mui.Grid container spacing={2} sx={{ paddingTop: 2 }}>
            <Mui.Grid item xs={12} md={12}>
              <Mui.Typography sx={{ fontWeight: 600 }}>
                Last Working
              </Mui.Typography>
              {/* <Mui.Typography {...infoStyle}>
                The last working date
              </Mui.Typography> */}
              <CustomTextBox
                type="date"
                selectedValue={deactivateEffectiveTo}
                // minDate={dateOfJoining}
                rules={{
                  ...register("effective_to_date", {
                    required: "Effective To Date is required",
                  }),
                }}
                updateDataFunction={handleEffectiveTo}
                errorMessage={
                  errors?.effective_to_date?.message
                    ? errors?.effective_to_date?.message
                    : ""
                }
                textBoxError={errors.effective_to_date ? true : false}
              />
            </Mui.Grid>
          </Mui.Grid>
          <Mui.Grid
            container
            spacing={2}
            sx={{ justifyContent: "flex-end", paddingTop: 1 }}
          >
            <Mui.Grid item>
              <CustomButton
                actionFuntion={() => handleDeactivateDialogBox()}
                actionName="No"
                typeName="submit"
              />
            </Mui.Grid>
            <Mui.Grid item>
              <CustomButton
                actionFuntion={() => deactivateEmployee()}
                actionName="Yes"
                typeName="submit"
                disableAction={
                  openDeactivatePopup && buttonLoader ? buttonLoader : false
                }
              />
            </Mui.Grid>
          </Mui.Grid>
        </GlobalDialogBox>
        <GlobalDialogBox
          open={openActivatePopup}
          handleCloseDialog={handleActivateDialogBox}
          title={"Activate the user"}
        >
          <Mui.Typography sx={{ fontSize: 22 }}>
            Do you want to activate the user?
          </Mui.Typography>
          <Mui.Grid container spacing={2} sx={{ paddingTop: 2 }}>
            <Mui.Grid item xs={12} md={12}>
              <Mui.Typography sx={{ fontWeight: 600 }}>
                Effective From
              </Mui.Typography>
              <Mui.Typography {...infoStyle}>
                The working date from
              </Mui.Typography>
              <CustomTextBox
                type="date"
                selectedValue={activateEffectiveFrom}
                // minDate={dateOfJoining}
                rules={{
                  ...register("effective_from_date", {
                    required: "Effective From Date is required",
                  }),
                }}
                updateDataFunction={handleEffectiveFrom}
                errorMessage={
                  errors?.effective_from_date?.message
                    ? errors?.effective_from_date?.message
                    : ""
                }
                textBoxError={errors.effective_from_date ? true : false}
              />
            </Mui.Grid>
          </Mui.Grid>
          <Mui.Grid
            container
            spacing={2}
            sx={{ justifyContent: "flex-end", paddingTop: 1 }}
          >
            <Mui.Grid item>
              <CustomButton
                actionFuntion={() => handleActivateDialogBox()}
                actionName="No"
                typeName="submit"
              />
            </Mui.Grid>
            <Mui.Grid item>
              <CustomButton
                actionFuntion={() => handleStartActivationReview()}
                actionName="Yes"
                disableAction={
                  openActivatePopup && buttonLoader ? buttonLoader : false
                }
                typeName="submit"
              />
            </Mui.Grid>
          </Mui.Grid>
        </GlobalDialogBox>

        <GlobalDialogBox
          open={openAssignmentCheckPopup}
          handleCloseDialog={handleAssignmentCheckClose}
          title={"Verify Assignments"}
        >
          <Mui.Typography sx={{ fontSize: 18 }}>
            Please check that all the previous details and assignments
            (Department, Shift, Salary, etc.) are still correct before
            activating this user. The user will be activated after you save the
            updates in the edit page.
          </Mui.Typography>
          <Mui.Grid
            container
            spacing={2}
            sx={{ justifyContent: "flex-end", paddingTop: 2 }}
          >
            <Mui.Grid item>
              <CustomButton
                actionFuntion={() => handleAssignmentCheckClose()}
                actionName="Review Assignments"
                typeName="button"
              />
            </Mui.Grid>
          </Mui.Grid>
        </GlobalDialogBox>

        <GlobalDialogBox
          open={openDialog}
          handleCloseDialog={handleReprocessDialogBox}
          title={"Reprocess Attendance"}
        >
          <Mui.Grid container alignItems="center">
            <Mui.Grid item xs={5}>
              <Mui.Typography sx={{ ...formKeyText, paddingTop: 0 }}>
                Date Range &nbsp;
              </Mui.Typography>
            </Mui.Grid>
            <Mui.Grid item xs={7}>
              <ApprovalDateRangeFilter
                startDate={reprocessStartDate}
                endDate={reprocessEndDate}
                dateRangeLabel={reprocessDateRangeLabel}
                onChange={handleReprocessDateRange}
                ranges={reprocessDateRanges}
                includeCurrentDate={false}
                showToday={false}
                allowFutureDate={false}
                minDate={dayjs(editDetails?.last_pay_date, "DD/MM/YYYY")
                  .add(1, "day")
                  .toDate()}
                sx={{ minHeight: 46, width: "100%" }}
              />
            </Mui.Grid>
          </Mui.Grid>
          <Mui.Grid
            container
            spacing={2}
            sx={{ justifyContent: "flex-end", paddingTop: 1 }}
          >
            <Mui.Grid item>
              <CustomButton
                actionFuntion={() => handleReprocessDialogBox()}
                actionName="No"
                typeName="button"
              />
            </Mui.Grid>
            <Mui.Grid item>
              <CustomButton
                actionFuntion={handleReprocessConfirm}
                actionName="Yes"
                disableAction={
                  openDialog && buttonLoader ? buttonLoader : false
                }
                typeName="button"
              />
            </Mui.Grid>
          </Mui.Grid>
        </GlobalDialogBox>
        <Mui.Drawer
          anchor="right"
          open={openActivePeriodDialog}
          onClose={() => handleActivePeriodClose()}
          sx={{
            "& .MuiDrawer-paper": {
              width: "680px",
              maxWidth: "90vw",
              backgroundColor: themes?.whiteColor,
            },
          }}
        >
          <Mui.Paper
            elevation={0}
            sx={{
              borderBottom: `1px solid ${themes?.miliedLightGray}`,
              borderRadius: 0,
            }}
          >
            <Mui.Box
              display="flex"
              flexDirection="column"
              alignItems="flex-start"
              sx={{ padding: 1, backgroundColor: themes?.sidebarDivider }}
            >
              <Mui.Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  borderRadius: "50%",
                }}
              >
                <Mui.Box sx={{ ml: 1 }}>
                  <Mui.IconButton
                    onClick={handleActivePeriodClose}
                    sx={{
                      color: themes?.grayColor,
                      "&:hover": {
                        backgroundColor: themes?.whiteColor,
                      },
                    }}
                  >
                    <DoubleArrow />
                  </Mui.IconButton>
                </Mui.Box>
              </Mui.Box>

              <Mui.Box sx={{ ml: 2 }}>
                <Mui.Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Employment Period
                </Mui.Typography>
                <Mui.Typography variant="body2" color="text.secondary">
                  User active and inactive period details
                </Mui.Typography>
              </Mui.Box>
            </Mui.Box>
          </Mui.Paper>
          <ActivePeriod
            userId={editDetails?.user_id}
            onClose={handleActivePeriodClose}
          />
        </Mui.Drawer>
        {bulkReprocess && (
          <BulkReprocess
            initialCompanyId={topbarCompanyId || adminCompanyId}
            expanded={bulkReprocessExpanded}
            onToggleExpand={() => setBulkReprocessExpanded((value) => !value)}
            setUserProcess={setUserProcess}
            onClose={() => setBulkReprocess(false)}
            reCallApi={() => setFetchUserDetails(true)}
            setBulkReprocess={setBulkReprocess}
            bulkReprocess={bulkReprocess}
            onTaskStart={(taskData) => {
              setTaskProgress({
                active: true,
                progress: 0,
                message: taskData.message || "Starting update...",
                status: "PENDING",
                title: "Bulk Reprocessing",
                initiatedBy: "User",
                estimatedTime: calculateRemainingTime(0),
                shiftCategoryName: "User Details",
                taskId: taskData.taskId,
                created_at: taskData.created_at ?? null,
                updated_at: taskData.updated_at ?? null,
                duration: taskData.duration ?? null,
                image_url: taskData.image_url ?? null,
              });

              if (taskData.taskId) {
                setTimeout(() => pollTaskStatus(taskData.taskId), 2000);
              }
            }}
            onTaskUpdate={(updates) => {
              setTaskProgress((prev) => {
                const newProgress = updates.progress ?? prev.progress;
                return {
                  ...prev,
                  ...updates,
                  active: true,
                  progress: newProgress,
                  estimatedTime: calculateRemainingTime(newProgress),
                  taskId: prev.taskId || updates.taskId || null,
                };
              });
            }}
            onTaskComplete={() => {
              setTaskProgress({
                active: false,
                progress: 0,
                message: "",
                status: null,
                title: "Bulk Reprocessing",
                initiatedBy: "",
                estimatedTime: "",
                shiftCategoryName: "",
                taskId: null,
                created_at: null,
                updated_at: null,
                duration: null,
                image_url: null,
              });

              if (isViewPanelOpen) {
                setIsViewPanelOpen(false);
              }
            }}
          />
        )}
      </React.Fragment>
    </>
  );
};
